import React, { Component } from 'react'
import DataGrid, { Column } from 'react-data-grid'
import { withTranslation, WithTranslation } from 'react-i18next'
import { connect, ConnectedProps } from 'react-redux'
import { NavLink, RouteComponentProps } from 'react-router-dom'
import { FrontendSettings } from '../../../../helpers/FrontendSettings'
import * as customerActions from '../../../../redux/actions/customerActions'
import { wrapQuery } from '../../../../redux/api/apiQuery'
import { customersApi } from '../../../../redux/api/customerApi'
import { State } from '../../../../redux/reducers/initialState'
import { FrontendSettingsKeys } from '../../../../types/enums'
import { CustomerViewModel, TenantViewModel } from '@ail/customers-api'
import { ICustomerViewModel } from '../../../../types/interfaces'
import Alert from '../../../common/Alert/Alert'
import './AssignToGroup.scss'
import { Loader } from '../../../common/Loader/Loader'

interface IAlertState {
  visible: boolean
  message: string
  title: string
}

interface IBreadCrumb {
  name: string
  id: string
}

type IAssignCustomerToTenantGridProps = ConnectedProps<typeof connector> & WithTranslation & RouteComponentProps

interface IAssignCustomerToTenantGridState {
  customers: CustomerViewModel[]
  selectedTenantId: string
  selectedCustomerId?: string
  gridCols: Array<Column<IAssignCustomerToTenantGridRow>>
  gridRows: IAssignCustomerToTenantGridRow[]
  gridRowsToRender: IAssignCustomerToTenantGridRow[]
  searchString: string
  searchStringToUse: string
  breadCrumbs: IBreadCrumb[]
  deleteAlertState: IAlertState
  customerToDelete?: ICustomerViewModel
  showDeleteConfirm: boolean
  loading: boolean
  height: number
  debounceTimeout: number | null
  error: boolean
  tenants: TenantViewModel[]
}

interface IAssignCustomerToTenantGridRow {
  id: string
  reference: string
  address: string
  name: string
  vatNumber?: string
  search: string
}

enum AssignCustomerToTenantGridColumnKeyEnum {
  name = 'name',
  address = 'address',
  contactInfo = 'contactInfo',
  reference = 'reference',
  source = 'source',
  vatNumber = 'vatNumber',
  actions = 'actions',
}

class AssignToGroup extends Component<IAssignCustomerToTenantGridProps, IAssignCustomerToTenantGridState> {
  constructor(props: IAssignCustomerToTenantGridProps) {
    super(props)

    this.state = {
      customers: [],
      height: window.innerHeight,
      loading: false,
      searchString: '',
      searchStringToUse: '',
      deleteAlertState: {
        message: 'nothing',
        title: 'nothing',
        visible: false,
      },
      selectedTenantId: FrontendSettings.get(FrontendSettingsKeys.preselectedTenantId) || '',
      gridCols: [],
      gridRows: [],
      gridRowsToRender: [],
      breadCrumbs: [],
      showDeleteConfirm: false,
      debounceTimeout: null,
      error: false,
      tenants: [],
    }
  }

  componentDidMount() {
    this.updateDimensions()

    window.addEventListener('resize', () => {
      this.updateDimensions()
    })

    this.setState({
      loading: true,
    })
    this.props.loadTenants().then((tenants) => {
      this.setState({ tenants })
      this.props
        .loadUnassignedRootCustomers()
        .then((customers) => {
          this.setState({
            customers,
            loading: false,
          })
        })
        .catch(() => {
          this.setState({ loading: false, error: true })
        })
    })
  }

  componentDidUpdate(oldProps: IAssignCustomerToTenantGridProps, oldState: IAssignCustomerToTenantGridState) {
    if (this.props.t !== oldProps.t) {
      this.generateGridCOls()
    }

    if (this.state.customers !== oldState.customers) {
      this.generateGridCOls()
      this.generateCustomerRows()
    }

    if (this.props.allCustomers !== oldProps.allCustomers) {
      this.setState({
        loading: false,
      })
    }

    const { searchStringToUse, selectedCustomerId } = this.state

    if (selectedCustomerId !== oldState.selectedCustomerId && !!selectedCustomerId) {
      this.assingnToSelectedTenant()
    }

    if (oldState.gridRows !== this.state.gridRows || searchStringToUse !== oldState.searchStringToUse) {
      this.generateCustomerRowsForRendering(searchStringToUse)
    }
  }

  render() {
    const { t } = this.props
    const { tenants } = this.state
    const { selectedTenantId, loading, searchString } = this.state
    return (
      <>
        {!this.state.error && (
          <div className="popAssignToGroupArea">
            <Loader blocking={loading}>
              <NavLink to="/UserManagement/groups" className="closeBtn">
                <i className="icon-close"></i>
              </NavLink>

              <div className="itemTitle">
                <div className="leftItemTitle" style={{ display: 'flex' }}>
                  <i className="icon-assign"></i>
                  {tenants.map((obj) => obj.id === selectedTenantId && ` ${t('ASSIGN_COMPANIES_TO')} ${obj.name}`)}
                </div>
                <div className="search-wrap">
                  <input
                    name={`fieldnameveryunique${new Date().getTime()}`}
                    autoComplete="none"
                    list="autocompleteOff"
                    autoFocus={true}
                    className="filterCompanies"
                    type="text"
                    onChange={(e) => this.searchChanged(e)}
                    onKeyDown={(e) => this.onKeyDownEvent(e)}
                    value={searchString}
                    placeholder={t('FIND_CUSTOMER_BY_KEYWORD')}
                  />
                  <button title={t('CLEAR')} className="clr-btn" onClick={() => this.searchChanged()}>
                    <span className="icon-clear" />
                  </button>
                  <i className="icon-search" />
                </div>
              </div>
              <div className="companiesToAssignGrid">
                <DataGrid
                  className="companiesToAssign rdg-light"
                  columns={this.state.gridCols}
                  rows={this.state.gridRowsToRender}
                  rowHeight={45}
                />
              </div>
            </Loader>
          </div>
        )}
        <Alert
          title={t('ERROR')}
          message={t('DEFAULT_ERROR_MSG')}
          visible={this.state.error}
          onClose={() => this.redirectBackToGroups()}
        />
        <div className="blur"></div>
      </>
    )
  }

  private onKeyDownEvent(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      this.searchChanged(e, true)
      return
    }

    if (e.key === 'Backspace' && this.state.searchString.length === 0) {
      e.preventDefault()
    }
  }

  private searchChanged(
    event?: React.ChangeEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>,
    instant?: boolean
  ) {
    const { debounceTimeout } = this.state
    const value = event ? event.currentTarget.value : ''
    this.setState({
      searchString: value,
    })
    if (debounceTimeout != null) {
      window.clearTimeout(debounceTimeout)
      this.setState({
        debounceTimeout: null,
      })
    }

    if (value && !instant) {
      this.setState({
        debounceTimeout: window.setTimeout(() => {
          this.setState({
            searchStringToUse: this.state.searchString.toLowerCase(),
            loading: true,
          })
          setTimeout(() => {
            this.setState({
              loading: false,
            })
          }, 300)
        }, 2000),
      })
    } else {
      this.setState({
        searchStringToUse: value,
        loading: true,
      })
      setTimeout(() => {
        this.setState({
          loading: false,
        })
      }, 300)
    }
  }

  private updateDimensions() {
    const c = 220
    const height = window.innerHeight > c ? window.innerHeight - c : window.innerHeight
    this.setState({
      height,
    })
  }

  private selectedCustomerAndOpenAssignModal(selectedCustomerId: string) {
    this.setState({
      selectedCustomerId,
    })
  }

  private assingnToSelectedTenant() {
    const { selectedCustomerId, selectedTenantId } = this.state
    if (!selectedCustomerId) return
    this.setState({
      loading: true,
    })
    this.props.patchUnassignedRootCustomerTenant(selectedCustomerId, selectedTenantId).then(() => {
      this.props.loadUnassignedRootCustomers().then((customers) => {
        this.setState({ customers })
        this.props.loadAllCustomers()
      })
    })
  }

  private generateGridCOls() {
    const { t } = this.props

    const selectedCustomerAndOpenAssignModal = (id: string) => {
      this.selectedCustomerAndOpenAssignModal(id)
    }

    this.setState({
      gridCols: [
        {
          key: AssignCustomerToTenantGridColumnKeyEnum.name,
          name: t('NAME'),
          resizable: true,
          renderCell({ row }) {
            return (
              <div title={row.name} style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                <span>{row.name}</span>
              </div>
            )
          },
        },
        {
          key: AssignCustomerToTenantGridColumnKeyEnum.reference,
          name: t('REFERENCE'),
        },

        {
          key: AssignCustomerToTenantGridColumnKeyEnum.vatNumber,
          name: t('VAT_NUMBER'),
        },
        {
          key: AssignCustomerToTenantGridColumnKeyEnum.address,
          name: t('ADDRESS'),
        },

        {
          key: AssignCustomerToTenantGridColumnKeyEnum.actions,
          name: t('ACTIONS'),
          renderCell({ row }) {
            return (
              <button className="assignBtn" onClick={() => selectedCustomerAndOpenAssignModal(row.id)}>
                <i className="icon-add-user"></i> {t('ASSIGN_TO_TENANT')}
              </button>
            )
          },
        },
      ],
    })
  }

  private redirectBackToGroups() {
    this.props.history.push('/UserManagement/groups')
  }

  private generateCustomerRows() {
    const gridRows: IAssignCustomerToTenantGridRow[] = this.state.customers
      .toSorted((a, b) => a.name?.localeCompare(b.name))
      .map((c) => {
        const address = `${c.primary?.addresses?.[0]?.state || ''} ${c.primary?.addresses?.[0]?.city || ''} ${
          c.primary?.addresses?.[0]?.postalCode || ''
        } ${c.primary?.addresses?.[0]?.street || ''}`
        const ref = c.reference?.split(':') ?? []
        let reference: string
        if (ref.length > 1) {
          reference = ref[1]
        } else {
          reference = ref[0]
        }
        const search = `${address} ${c.name || ''} ${c.vatNumber || ''} ${reference || ''}`.toLowerCase()
        return {
          id: c.id,
          reference,
          name: c.name,
          vatNumber: c.vatNumber,
          address,
          search,
        }
      })

    this.setState({
      gridRows,
    })
  }

  private generateCustomerRowsForRendering(search?: string) {
    const { gridRows } = this.state
    const gridRowsToRender = search ? gridRows.filter((r) => r.search.indexOf(search) >= 0) : gridRows

    this.setState({
      gridRowsToRender,
    })
  }
}

/**
 * decide what part of state should be exposed
 * @param state  redux state
 * @param ownProps props of component
 */
function mapStateToProps({ customers }: State) {
  return {
    allCustomers: customers.customers,
  }
}

/**
 * What action should be passed to component
 */
const mapDispatchToProps = {
  loadTenants: wrapQuery(() => customersApi.endpoints.getTenants.initiate()),
  loadUnassignedRootCustomers: wrapQuery(() => customersApi.endpoints.getUnassignedCustomers.initiate()),
  patchUnassignedRootCustomerTenant: wrapQuery((customerId: string, tenantId?: string) =>
    customersApi.endpoints.patchCustomerTenant.initiate({ customerId, tenantId })
  ),
  loadAllCustomers: customerActions.loadAllCustomers,
}
const connector = connect(mapStateToProps, mapDispatchToProps)
// eslint-disable-next-line react-refresh/only-export-components
export default connector(withTranslation('common')(AssignToGroup))
